import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Upload, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { compressImage } from "@/lib/image";
import api from "@/lib/api";
import { Logo, IDButton, PERSONAS, FOCUS_AREAS, SIGNATURE_QUESTION } from "@/marketing/brand";
import { IDField } from "@/components/AuthShell";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, loading, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [archetypes, setArchetypes] = useState([]);
  const [f, setF] = useState({ persona: "", title: "", location: "", focus_areas: [], word_of_year: "", intention: "", picture: null, archetype: "career" });

  useEffect(() => { api.get("/departments/archetypes").then(({ data }) => setArchetypes(data.archetypes || [])).catch(() => {}); }, []);

  if (!loading && !user) return <Navigate to="/login" replace />;

  const toggleFocus = (area) => setF((p) => ({ ...p, focus_areas: p.focus_areas.includes(area) ? p.focus_areas.filter((x) => x !== area) : [...p.focus_areas, area] }));

  const onPhoto = async (file) => {
    if (!file) return;
    try { const d = await compressImage(file); setF((p) => ({ ...p, picture: d })); }
    catch { toast.error("Could not process that image."); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      try { await api.post("/departments/select-archetype", { archetype: f.archetype || "career" }); } catch { /* non-blocking */ }
      await updateProfile({ ...f, onboarded: true });
      toast.success("Your profile is ready.");
      navigate("/dashboard");
    } catch { toast.error("Could not save your profile."); }
    finally { setBusy(false); }
  };

  const STEPS = ["Who you are", "Your season", "Your focus", "Your intention"];
  const LAST = STEPS.length - 1;
  const canNext = step === 0 ? !!f.persona : step === 1 ? !!f.archetype : step === 2 ? f.focus_areas.length > 0 : true;

  return (
    <div className="id-surface min-h-screen">
      <div className="mx-auto max-w-[720px] px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between">
          <Logo className="h-9" />
          <span className="id-sans text-xs uppercase tracking-[0.2em] text-[var(--id-charcoal)]/50">Step {step + 1} of {STEPS.length}</span>
        </div>

        {/* progress */}
        <div className="mt-6 flex gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? "bg-[var(--id-gold)]" : "bg-[var(--id-mist)]"}`} />
              <p className={`id-sans mt-2 text-[11px] uppercase tracking-[0.14em] ${i <= step ? "text-[var(--id-charcoal)]" : "text-[var(--id-charcoal)]/40"}`}>{s}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 id-fade-up" key={step}>
          {step === 0 && (
            <div>
              <h1 className="id-serif text-[clamp(2rem,4.5vw,3rem)] leading-[1.06] text-[var(--id-charcoal)]">Which woman are you today?</h1>
              <p className="id-sans mt-3 text-[15px] text-[var(--id-charcoal)]/65">This shapes how your system speaks to you. You can change it anytime.</p>
              <div className="mt-7 grid gap-3">
                {PERSONAS.map((p) => (
                  <button key={p.key} type="button" onClick={() => setF({ ...f, persona: p.name })} data-testid={`onboard-persona-${p.key}`}
                    className={`text-left rounded-2xl border p-5 transition-colors ${f.persona === p.name ? "border-[var(--id-gold)] bg-[var(--id-gold)]/8" : "border-[var(--id-mist)] bg-white hover:border-[var(--id-charcoal)]/30"}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="id-serif text-xl text-[var(--id-charcoal)]">{p.name}</h3>
                      {f.persona === p.name && <Check size={18} className="text-[var(--id-gold)]" />}
                    </div>
                    <p className="id-sans mt-1 text-[14px] text-[var(--id-charcoal)]/65">{p.line}</p>
                  </button>
                ))}
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <IDField label="Role or title (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Founder & Director" />
                <IDField label="Where you live (optional)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. Atlanta, GA" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h1 className="id-serif text-[clamp(2rem,4.5vw,3rem)] leading-[1.06] text-[var(--id-charcoal)]">What season are you in?</h1>
              <p className="id-sans mt-3 text-[15px] text-[var(--id-charcoal)]/65">Your season shapes your five departments. Faith &amp; Character stays fixed — everything else you can rename and reweight anytime in Settings.</p>
              <div className="mt-7 grid gap-3">
                {archetypes.map((a) => (
                  <button key={a.id} type="button" onClick={() => setF({ ...f, archetype: a.id })} data-testid={`onboard-archetype-${a.id}`}
                    className={`text-left rounded-2xl border p-5 transition-colors ${f.archetype === a.id ? "border-[var(--id-gold)] bg-[var(--id-gold)]/8" : "border-[var(--id-mist)] bg-white hover:border-[var(--id-charcoal)]/30"}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="id-serif text-xl text-[var(--id-charcoal)]">{a.label}</h3>
                      {f.archetype === a.id && <Check size={18} className="text-[var(--id-gold)]" />}
                    </div>
                    <p className="id-sans mt-1 text-[14px] text-[var(--id-charcoal)]/65">{a.description}</p>
                    <p className="id-sans mt-2 text-[12px] text-[var(--id-charcoal)]/50">{(a.departments || []).map((d) => d.name).join(" · ")}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="id-serif text-[clamp(2rem,4.5vw,3rem)] leading-[1.06] text-[var(--id-charcoal)]">What deserves your focus this season?</h1>
              <p className="id-sans mt-3 text-[15px] text-[var(--id-charcoal)]/65">Choose the life divisions you want to steward. Select all that apply.</p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {FOCUS_AREAS.map((area) => {
                  const on = f.focus_areas.includes(area);
                  return (
                    <button key={area} type="button" onClick={() => toggleFocus(area)} data-testid={`onboard-focus-${area}`}
                      className={`id-sans rounded-full border px-5 py-2.5 text-[14px] transition-colors ${on ? "border-[var(--id-gold)] bg-[var(--id-charcoal)] text-[var(--id-ivory)]" : "border-[var(--id-mist)] bg-white text-[var(--id-charcoal)]/75 hover:border-[var(--id-charcoal)]/30"}`}>
                      {on && <Check size={13} className="mr-1.5 inline" />}{area}
                    </button>
                  );
                })}
              </div>
              <div className="mt-7"><IDField label="Your word of the year (optional)" value={f.word_of_year} onChange={(e) => setF({ ...f, word_of_year: e.target.value })} placeholder="e.g. Rooted" /></div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="id-serif text-[clamp(1.9rem,4vw,2.7rem)] leading-[1.1] text-[var(--id-charcoal)]">“{SIGNATURE_QUESTION}”</h1>
              <p className="id-sans mt-3 text-[15px] text-[var(--id-charcoal)]/65">Write it in your own words. This becomes the anchor of your daily practice.</p>
              <textarea value={f.intention} onChange={(e) => setF({ ...f, intention: e.target.value })} data-testid="onboard-intention" rows={4}
                placeholder="I am becoming… and today my best energy belongs to…"
                className="id-sans mt-6 w-full rounded-2xl border border-[var(--id-mist)] bg-white px-5 py-4 text-[16px] leading-relaxed text-[var(--id-charcoal)] outline-none focus:border-[var(--id-gold)] focus:ring-2 focus:ring-[var(--id-gold)]/20" />
              <div className="mt-6">
                <span className="id-sans text-[12px] font-medium uppercase tracking-[0.14em] text-[var(--id-charcoal)]/60">Profile photo (optional)</span>
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-[var(--id-mist)]">
                    {f.picture ? <img src={f.picture} alt="" className="h-full w-full object-cover" /> : <span className="id-serif text-2xl text-[var(--id-walnut)]">{(user?.name || "?")[0]}</span>}
                  </div>
                  <label className="id-sans inline-flex cursor-pointer items-center gap-2 rounded-full border border-[var(--id-charcoal)]/25 px-5 py-2.5 text-[13px] text-[var(--id-charcoal)] hover:bg-[var(--id-charcoal)]/5">
                    <Upload size={15} /> Upload photo
                    <input type="file" accept="image/*" hidden onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="onboard-photo" />
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 flex items-center justify-between">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="id-sans inline-flex items-center gap-2 text-sm text-[var(--id-charcoal)]/70 hover:text-[var(--id-charcoal)]"><ArrowLeft size={16} /> Back</button>
          ) : <span />}
          {step < LAST ? (
            <IDButton as="button" onClick={() => canNext && setStep(step + 1)} variant="solid" disabled={!canNext} testid="onboard-next">Continue <ArrowRight size={16} /></IDButton>
          ) : (
            <IDButton as="button" onClick={finish} variant="gold" disabled={busy} testid="onboard-finish">{busy ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <>Enter my dashboard <ArrowRight size={16} /></>}</IDButton>
          )}
        </div>
      </div>
    </div>
  );
}
